import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardDateProvider } from '../context/DashboardDateContext';

const ROUTE_OPERATION_MAP = {
  '/fund-management/sources': 'FUND_SOURCE_LIST',
  '/fund-management/transactions': 'FUND_ACCOUNT_VIEW_TRANSACTIONS',
  '/fund-management/retirements': 'FUND_ACCOUNT_VIEW_DETAIL',
  '/fund-management/approvals': 'FUND_ACCOUNT_VIEW_DETAIL'
};

export const MfaProtectedRoute = () => {
  const { 
    isAuthenticated, 
    user, 
    loading, 
    isInitialized, 
    mfaVerifiedForSession,
    isOperationMfaVerified
  } = useAuth();
  
  const location = useLocation();
  
  if (loading || !isInitialized) {
    console.log('MfaProtectedRoute: Loading or not initialized...');
    return <div>Loading...</div>;
  }

  console.log('MfaProtectedRoute: User:', user);
  console.log('MfaProtectedRoute: isAuthenticated:', isAuthenticated());
  console.log('MfaProtectedRoute: user.mfa_enabled:', user?.mfa_enabled);
  console.log('MfaProtectedRoute: user.two_factor_enabled:', user?.two_factor_enabled);
  console.log('MfaProtectedRoute: mfaVerifiedForSession:', mfaVerifiedForSession);
  console.log('MfaProtectedRoute: Current path:', location.pathname);

  // First check: Is the user authenticated at all?
  if (!isAuthenticated()) {
    console.log('MfaProtectedRoute: Not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // Determine if this is a fund management route
  const isFundManagementRoute = location.pathname.startsWith('/fund-management') || 
                               ROUTE_OPERATION_MAP[location.pathname];
  
  const requiredOperation = ROUTE_OPERATION_MAP[location.pathname] || 'FUND_MANAGEMENT';
  
  // For fund management routes only, check MFA requirements
  if (isFundManagementRoute) {
    console.log('This is a fund management route requiring MFA verification');
    
    // Case 1: User doesn't have MFA set up yet - send to setup
    if (user && !user.mfa_enabled) {
      console.log('MFA not set up yet, redirecting to MFA setup');
      return (
        <Navigate
          to="/mfa-setup"
          state={{
            operationType: requiredOperation,
            returnUrl: location.pathname,
            forceSetup: true,
            isMfaSetup: true
          }}
          replace
        />
      );
    }
    
    // Case 2: User has MFA enabled but operation not verified - send to verification
    const isOperationVerified = isOperationMfaVerified(requiredOperation);
    console.log(`MfaProtectedRoute: Operation ${requiredOperation} verified:`, isOperationVerified);
    
    if (user && user.mfa_enabled && !isOperationVerified) {
      console.log('MFA required for this operation, redirecting to MFA verification');
      return (
        <Navigate
          to="/mfa-setup"
          state={{
            operationType: requiredOperation,
            returnUrl: location.pathname,
            isMfaSetup: false  // This indicates verification mode, not setup
          }}
          replace
        />
      );
    }
  } else {
    console.log('Route does not require fund management MFA verification');
  }

  // If we get here, either:
  // 1. This is not a fund management route, or
  // 2. User has MFA set up and operation is verified
  return (
    <DashboardDateProvider>
      <Outlet />
    </DashboardDateProvider>
  );
};

export default MfaProtectedRoute;
